import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Class } from '@/types/database';
import { format } from 'date-fns';
import { Calendar, MapPin, Video, Users, Star, Layers, Rocket } from 'lucide-react';
import { formatSessionDays } from '@/lib/sessionDateUtils';
import { useFoundingInstructor } from '@/hooks/useFoundingInstructor';
import { FoundingInstructorBadge } from '@/components/badges/FoundingInstructorBadge';

// Category placeholder images
import cookingImage from '@/assets/category-cooking.webp';
import artImage from '@/assets/category-art.webp';
import musicImage from '@/assets/category-music.jpg';
import fitnessImage from '@/assets/category-fitness.jpg';
import techImage from '@/assets/category-tech.jpg';
import photographyImage from '@/assets/category-photography.jpg';

const categoryImageMap: Record<string, string> = {
  'cooking': cookingImage,
  'art': artImage,
  'music': musicImage,
  'fitness': fitnessImage,
  'tech': techImage,
  'photography': photographyImage,
  'wellness': fitnessImage,
  'dance': musicImage,
  'design': artImage,
  'languages': techImage,
};

interface ClassCardProps {
  classItem: Class;
}

export function ClassCard({ classItem }: ClassCardProps) {
  const { data: isFoundingInstructor } = useFoundingInstructor(classItem.instructor_id);

  const getLocationIcon = () => {
    if (classItem.location_type === 'virtual') return <Video className="h-3.5 w-3.5" />;
    if (classItem.location_type === 'hybrid') return <><MapPin className="h-3.5 w-3.5" /><Video className="h-3.5 w-3.5" /></>;
    return <MapPin className="h-3.5 w-3.5" />;
  };

  const getLocationText = () => {
    if (classItem.location_type === 'virtual') return 'Virtual';
    if (classItem.location_type === 'hybrid') return classItem.city || 'Hybrid';
    return classItem.city || classItem.address || 'In Person';
  };

  const getInitials = (name: string | null) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Get placeholder image based on category
  const getPlaceholderImage = () => {
    const categorySlug = classItem.category?.slug;
    if (categorySlug && categoryImageMap[categorySlug]) {
      return categoryImageMap[categorySlug];
    }
    return null;
  };

  const placeholderImage = getPlaceholderImage();

  return (
    <Link to={`/class/${classItem.slug || classItem.id}`}>
      <Card className="group overflow-hidden hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 h-full border-border/50 hover:border-primary/30">
        {/* Image */}
        <div className="relative aspect-[16/10] overflow-hidden bg-muted">
          {classItem.image_url ? (
            <img
              src={classItem.image_url}
              alt={classItem.title}
              className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
              decoding="async"
              width="673"
              height="378"
            />
          ) : placeholderImage ? (
            <img
              src={placeholderImage}
              alt={classItem.title}
              className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-500 opacity-80"
              loading="lazy"
              decoding="async"
              width="673"
              height="378"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-accent/20">
              <span className="text-5xl font-serif text-primary/40">
                {classItem.title.charAt(0)}
              </span>
            </div>
          )}
          
          {/* Category Badge */}
          {classItem.category && (
            <Badge variant="secondary" className="absolute top-3 left-3 bg-background/95 backdrop-blur-sm shadow-sm">
              {classItem.category.name}
            </Badge>
          )}

          {/* Launching Soon Badge */}
          {classItem.is_presell && classItem.status === 'presell' && (
            <Badge className="absolute top-3 left-3 bg-amber-500 hover:bg-amber-500 text-white shadow-sm" style={classItem.category ? { top: '2.75rem' } : undefined}>
              <Rocket className="h-3 w-3 mr-1" />
              Launching Soon
            </Badge>
          )}

          {/* Multi-session Badge */}
          {classItem.is_multi_session && (
            <Badge variant="secondary" className="absolute bottom-3 left-3 bg-background/95 backdrop-blur-sm shadow-sm">
              <Layers className="h-3 w-3 mr-1" />
              {classItem.total_sessions} sessions
            </Badge>
          )}

          {/* Price Badge */}
          <div className="absolute top-3 right-3">
            <Badge className={`shadow-sm ${classItem.is_free ? 'bg-success hover:bg-success' : 'gradient-primary'}`}>
              {classItem.is_free ? 'Free' : `$${classItem.price}`}
            </Badge>
          </div>
        </div>

        <CardContent className="p-4">
          {/* Title */}
          <h3 className="font-serif text-lg font-medium line-clamp-2 group-hover:text-primary transition-colors mb-2">
            {classItem.title}
          </h3>

          {/* Instructor */}
          {classItem.instructor && (
            <div className="flex items-center gap-2 mb-3">
              <Avatar className="h-6 w-6 border border-border">
                <AvatarImage src={classItem.instructor.avatar_url || undefined} />
                <AvatarFallback className="text-xs bg-primary/10 text-primary">
                  {getInitials(classItem.instructor.full_name)}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm text-muted-foreground">
                {classItem.instructor.full_name}
              </span>
              {isFoundingInstructor && <FoundingInstructorBadge size="sm" />}
            </div>
          )}

          {/* Meta */}
          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5 text-primary/70" />
              {classItem.is_multi_session && classItem.session_end_date ? (
                <span>
                  {format(new Date(classItem.class_date), 'MMM d')} – {format(new Date(classItem.session_end_date), 'MMM d')}
                </span>
              ) : (
                format(new Date(classItem.class_date), 'MMM d, h:mm a')
              )}
            </div>
            {classItem.is_multi_session && classItem.session_days && classItem.session_days.length > 0 && (
              <div className="flex items-center gap-1">
                <Layers className="h-3.5 w-3.5" />
                {formatSessionDays(classItem.session_days)}
              </div>
            )}
            <div className="flex items-center gap-1">
              {getLocationIcon()}
              {getLocationText()}
            </div>
            <div className="flex items-center gap-1">
              <Users className="h-3.5 w-3.5" />
              {classItem.capacity} spots
            </div>
          </div>

          {/* Rating */}
          {classItem.average_rating && (
            <div className="flex items-center gap-1 mt-3 text-sm">
              <Star className="h-4 w-4 fill-primary text-primary" />
              <span className="font-medium">{classItem.average_rating.toFixed(1)}</span>
              {classItem.reviews_count && (
                <span className="text-muted-foreground">
                  ({classItem.reviews_count} reviews)
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}
